const SITE_ORIGIN = 'https://grupa.ai';
const PUBLIC_PAGE_KEYS = ['home', 'about', 'api', 'workspace', 'vision', 'manifesto', 'careers', 'journal', 'documentation', 'privacy', 'terms', 'innovators'];
const PRIVATE_PATHS = new Set(['/login', '/callback', '/dashboard', '/choose-plan', '/premium-download', '/download']);

function canonicalPath(value = '/') {
  const path = ('/' + String(value).split(/[?#]/)[0].replace(/^\/+/, '')).replace(/\/+$/, '') || '/';
  return path === '/desktop' ? '/' : path === '/about-us' ? '/about' : path;
}
function absoluteUrl(path = '/') {
  return /^https?:\/\//.test(path) ? path : SITE_ORIGIN + canonicalPath(path);
}
function absoluteShareImage(imagePath) {
  return imagePath ? new URL(imagePath, SITE_ORIGIN + '/').href : SITE_ORIGIN + '/logo192.png';
}
function buildSocialMetaTags({ title, description, image, path, type }) {
  return [
    { attr: 'name', key: 'description', content: description },
    { attr: 'property', key: 'og:type', content: type || (canonicalPath(path).startsWith('/blog/') ? 'article' : 'website') },
    { attr: 'property', key: 'og:site_name', content: 'GrupaAI' },
    { attr: 'property', key: 'og:title', content: title },
    { attr: 'property', key: 'og:description', content: description },
    { attr: 'property', key: 'og:url', content: absoluteUrl(path) },
    { attr: 'property', key: 'og:image', content: absoluteShareImage(image) },
    { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', content: title },
    { attr: 'name', key: 'twitter:description', content: description },
    { attr: 'name', key: 'twitter:image', content: absoluteShareImage(image) },
  ];
}
function escapeHtml(value = '') {
  return String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
}
function serializeJson(value) {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}
function publishedPosts(posts, now = Date.now()) {
  return posts.filter(post => !post.private && !post.status && !post.scheduledDate && !post.coverFile && Number.isFinite(Date.parse(post.publishedDate || post.date)) && Date.parse(post.publishedDate || post.date) <= now);
}
function publicEntries(pageMeta, posts, archives = [], now = Date.now()) {
  const pages = PUBLIC_PAGE_KEYS.filter(key => pageMeta[key]).map(key => ({ ...pageMeta[key], path: canonicalPath(pageMeta[key].path) }));
  const articles = publishedPosts([...posts, ...archives], now).map(post => ({
    path: `/blog/${post.slug}`, title: `${post.title} - GrupaAI Journal`, description: post.excerpt,
    image: post.image || pageMeta.journal.image, type: 'article', post,
  }));
  return [...pages, ...articles];
}
function sitemap(entries) {
  const paths = [...new Set(entries.map(entry => canonicalPath(entry.path)))].filter(path => !PRIVATE_PATHS.has(path));
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${paths.map(path => `  <url><loc>${escapeHtml(absoluteUrl(path))}</loc></url>`).join('\n')}\n</urlset>\n`;
}
function postalAddress(location) {
  return {
    '@type': 'PostalAddress',
    ...(location.addressLocality ? { addressLocality: location.addressLocality } : {}),
    ...(location.addressRegion ? { addressRegion: location.addressRegion } : {}),
    ...(location.addressCountry ? { addressCountry: location.addressCountry } : {}),
  };
}
function structuredData(meta, company, post) {
  if (PRIVATE_PATHS.has(canonicalPath(meta.path))) return null;
  const organizationId = `${SITE_ORIGIN}/#organization`;
  const websiteId = `${SITE_ORIGIN}/#website`;
  const founderId = `${SITE_ORIGIN}/#founder`;
  const url = absoluteUrl(meta.path);
  const offices = (company.offices || []).filter(office => office.addressLocality).map(office => ({
    '@type': 'Place',
    ...(office.name ? { name: office.name } : {}),
    address: postalAddress(office),
  }));
  const organization = {
    '@type': 'Organization', '@id': organizationId, name: company.name, url: SITE_ORIGIN + '/', description: company.description,
    ...(company.sameAs?.length ? { sameAs: company.sameAs } : {}),
    ...(company.foundingDate ? { foundingDate: company.foundingDate } : {}),
    ...(company.headquarters?.addressLocality ? { address: postalAddress(company.headquarters) } : {}),
    ...(offices.length ? { location: offices } : {}),
    ...(company.founder?.name ? { founder: { '@id': founderId } } : {}),
  };
  const graph = [organization, { '@type': 'WebSite', '@id': websiteId, name: company.name, url: SITE_ORIGIN + '/', publisher: { '@id': organizationId } }];
  if (company.founder?.name) {
    graph.push({ '@type': 'Person', '@id': founderId, name: company.founder.name,
      ...(company.founder.jobTitle ? { jobTitle: company.founder.jobTitle } : {}),
      ...(company.founder.url ? { url: new URL(company.founder.url, SITE_ORIGIN + '/').href } : {}),
      ...(company.founder.sameAs?.length ? { sameAs: company.founder.sameAs } : {}),
      worksFor: { '@id': organizationId },
    });
  }
  graph.push({ '@type': canonicalPath(meta.path) === '/about' ? 'AboutPage' : 'WebPage', '@id': url + '#webpage', url, name: meta.title, description: meta.description, isPartOf: { '@id': websiteId }, about: { '@id': organizationId } });
  if (post) {
    const date = new Date(Date.parse(post.publishedDate || post.date)).toISOString().slice(0, 10);
    const author = /^(?:GrupaAI|GrupaAI Pioneer AI Lab)$/.test(post.author) ? { '@id': organizationId } : { '@type': 'Person', name: post.author };
    graph.push({ '@type': 'BlogPosting', '@id': url + '#article', url, headline: post.title, description: post.excerpt,
      datePublished: date, author, publisher: { '@id': organizationId }, mainEntityOfPage: { '@id': url + '#webpage' },
      ...(post.image ? { image: absoluteUrl(post.image) } : {}),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

module.exports = { SITE_ORIGIN, PUBLIC_PAGE_KEYS, PRIVATE_PATHS, canonicalPath, absoluteUrl, absoluteShareImage, buildSocialMetaTags, escapeHtml, serializeJson, publishedPosts, publicEntries, sitemap, structuredData };
